/* eslint-disable camelcase */
import * as React from 'react'
import { Redirect } from 'react-router-dom'

import { DateInput, Form, Forms, Name, SSN, useFormsContext } from '@ally/forms'
import { Box, Button, upToMd } from '@ally/metronome-ui'
import {
  EnrollVerifyCustomerInfoProps,
  EnrollVerifyCustomerPayloadUnion,
} from '@ally/transmitigator'
import { FormikHelpers } from 'formik'

import { useAppState } from '../../AppStateProvider'
import { useHostServices } from '../../HostServices'
import { SSNModal, SomethingsNotRightModal } from '../../Modals'
import { useTransmit } from '../../TransmitContext'
import { FeatureFlag } from '../../Utils'
import { TrackingEvents } from '../../constants'
import log from '../../whisper'
import { ErrorBanner } from '../ErrorBanner'
import { InputWrapper } from '../Shared'

// Note: The order here is important!
export const CIAM_CUSTOMER_MISSING_ERROR_CODES = [
  'CIAM_E08_04_001',
  'CIAM_E08_03_002',
]
const CIAM_MFA_OPTIONS_AVAILABLE = 'CIAM_E08_00_004'

const fieldRef = React.createRef<HTMLInputElement>()

export const VerifyCustomerInfoErrorBanner: React.FC<Pick<
  EnrollVerifyCustomerInfoProps,
  'error'
>> = ({ error }) => {
  const { status } = useFormsContext()
  const { actions, logger } = useTransmit()
  const { featureFlags } = useHostServices()
  const { oaa_ole } = useAppState()
  const isOAAOLEDisabled: boolean = featureFlags.variation(
    FeatureFlag.DisableOAAOLE,
    false,
  )

  let errorCode = error
  // hack because transmit forces a reset of the journey
  if (!errorCode && CIAM_CUSTOMER_MISSING_ERROR_CODES.includes(status)) {
    errorCode = CIAM_CUSTOMER_MISSING_ERROR_CODES.find(code => code === status)
  }

  if (!errorCode) {
    return null
  }

  switch (errorCode) {
    case CIAM_CUSTOMER_MISSING_ERROR_CODES[0]: // CIAM_E08_04_001
      return (
        <SomethingsNotRightModal
          onDismiss={(isOpen: boolean, reason?: string): void => {
            if (!isOpen && reason !== 'unmount') {
              actions.onlineEnrollment({
                journey_version: 'v2',
                ...(isOAAOLEDisabled ? {} : { oaa_ole }),
              })
            }
          }}
        />
      )
    case CIAM_CUSTOMER_MISSING_ERROR_CODES[1]: // CIAM_E08_03_002
      return (
        <ErrorBanner mb="sm">
          We can&apos;t find a user with that information. Double-check that all
          fields are entered correctly.
        </ErrorBanner>
      )
    case 'CIAM_E04_03_025':
      return <ErrorBanner mb="sm">Enter a valid Last Name</ErrorBanner>
    case 'CIAM_E08_01_003':
      return <ErrorBanner mb="sm">Enter a valid Date of Birth</ErrorBanner>
    case 'CIAM_E08_01_015':
      return (
        <ErrorBanner mb="sm">
          You must be at least 18 to enroll online.
        </ErrorBanner>
      )
    case 'CIAM_E01_03_013':
      return (
        <ErrorBanner mb="sm">Enter a valid Social Security Number</ErrorBanner>
      )
    case 'CIAM_E01_08_001': // AAOS_ACCOUNT_VERIFICATION_ERROR
      return (
        <ErrorBanner mb="sm">
          We&apos;re having trouble finding your account. Double-check what you
          entered or give us a call for help.
        </ErrorBanner>
      )
    case CIAM_MFA_OPTIONS_AVAILABLE:
      logger?.track(TrackingEvents.InvalidMFAOptions)
      return <Redirect to="/security/error" push />
    default:
      log.error({
        message: [`Unknown error code returned from transmit: ${errorCode}`],
      })
      return (
        <ErrorBanner mb="sm">
          Something&apos;s not working. Try again later.
        </ErrorBanner>
      )
  }
}

export interface VerifyCustomerInfoFormProps {
  formId: string
  status: string
  error?: Error | string
}
export interface VerifyCustomerInfoSubmitProps {
  lastName?: string
  dateOfBirth: string
  ssn: string
  isAutoBusinessUser: boolean
}
export interface VerifyCustomerInfoFormContextProps {
  status: string
  error?: Error | string
  onComplete: (payload: EnrollVerifyCustomerPayloadUnion) => void
  isLastNameRemovalOLEEnabled: boolean
}

export const VerifyCustomerInfoForm: React.FC<VerifyCustomerInfoFormProps> = ({
  formId,
  status,
  error,
}) => {
  const { actions } = useTransmit()
  const { setStatus } = useFormsContext()
  const { featureFlags } = useHostServices()
  const { oaa_ole } = useAppState()

  const isLastNameRemovalOLEEnabled: boolean = featureFlags.variation(
    FeatureFlag.LastNameRemovalOLE,
    false,
  )

  const isOAAOLEDisabled: boolean = featureFlags.variation(
    FeatureFlag.DisableOAAOLE,
    false,
  )

  /**
   *  If a user receives this error code back it means the info entered does
   *  not match any records in FIS. Instead of allowing the user to simply
   *  resubmit the form, transmit terminates the journey entirely. This
   *  requires us to restart the journey to allow the user to retry, but it
   *  does not come without headaches of its own.
   */
  React.useEffect(() => {
    if (error && error.toString() === 'CIAM_E08_03_002') {
      log.debug({ message: 'User failed submit, restarting OLE journey' })

      // Use status to retain the CIAM error code for after the journey restart
      setStatus(error)

      actions.onlineEnrollment({
        journey_version: 'v2',
        ...(isOAAOLEDisabled ? {} : { oaa_ole }),
      })
    }
  }, [error])

  React.useEffect(() => {
    fieldRef.current?.focus()
  }, [])

  return (
    <Form id={formId}>
      <VerifyCustomerInfoErrorBanner error={error} />

      {!isLastNameRemovalOLEEnabled && (
        <InputWrapper mb="md">
          <Name
            variant="Last Name"
            name="lastName"
            data-allytmfn="lastName"
            required
            data-testid="enroll-last-name"
            contextualHelp={{
              content: 'As it appears on your account',
              variant: 'stacked',
            }}
          />
        </InputWrapper>
      )}

      <InputWrapper mb="md">
        <DateInput
          label="Date of birth"
          name="dateOfBirth"
          data-allytmfn="dateOfBirth"
          hidePlaceholder
          required
          data-testid="enroll-date"
          contextualHelpContent="mm/dd/yyyy"
          ref={fieldRef}
        />
      </InputWrapper>

      <Box mb="lg">
        <InputWrapper mb="md" width="235px">
          <SSN
            label="Social security number"
            name="ssn"
            data-allytmfn="ssn"
            required
            data-testid="enroll-ssn"
          />
        </InputWrapper>
        <Box
          mt={upToMd<string, string>('none', 'md')}
          mb={upToMd<string, string>('md', 'none')}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          <SSNModal textBodyType="enroll" />
        </Box>
      </Box>

      <Button
        text="Continue"
        type="submit"
        variant="primary"
        loading={status === 'Loading'}
        fullWidthMd
        allytmln="continue"
      />
    </Form>
  )
}

export const VerifyCustomerInfoFormContext: React.FC<VerifyCustomerInfoFormContextProps> = ({
  onComplete,
  error,
  status,
  isLastNameRemovalOLEEnabled,
}) => {
  const formId = 'verify-customer-info-form'

  const initialInfoFormValues: VerifyCustomerInfoSubmitProps = {
    lastName: '',
    dateOfBirth: '',
    ssn: '',
    isAutoBusinessUser: false,
  }
  const handleSubmit = (
    formValues: VerifyCustomerInfoSubmitProps,
    { setSubmitting, setStatus }: FormikHelpers<VerifyCustomerInfoSubmitProps>,
  ): void => {
    const {
      lastName,
      dateOfBirth,
      ssn,
      isAutoBusinessUser,
    } = formValues as VerifyCustomerInfoSubmitProps
    const basePayload = {
      dateOfBirth,
      taxID: ssn,
      isAutoBusinessUser, // TODO: handle Auto Business logic
    } as EnrollVerifyCustomerPayloadUnion
    const payload = isLastNameRemovalOLEEnabled
      ? basePayload
      : ({ ...basePayload, lastName } as EnrollVerifyCustomerPayloadUnion)
    onComplete(payload)

    // Since onComplete does not return a promise, we need to immediately reset the
    // isSubmitting flag in order to be able to resubmit the form in case of error
    setSubmitting(false)
    setStatus(undefined) // reset any possible error stored in formiks status
  }
  return (
    <Forms
      formId={formId}
      initialValues={initialInfoFormValues}
      onSubmit={handleSubmit}
    >
      <VerifyCustomerInfoForm formId={formId} error={error} status={status} />
    </Forms>
  )
}
